import {
  CommercialIndustrialRoutes,
  HdbResaleRoutes,
  OthersRoutes,
  PreviewRoutes,
  PrivateResaleRoutes,
  Routes,
} from './routes';
import * as config from '@/config/config.json';
import * as baseConfig from '@/config/base-config.json';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export enum FinancingLoanType {
  sixtyFiveYearLoan = 65,
  seventyFiveYearLoan = 75,
}

// for Private Residential
export const FINANCING_YEAR_65 = {
  LOAN_TENURE: {
    DEFAULT: config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
    MAX: config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
    SLIDER_LABELS: generateSliderSteps(
      0,
      config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
      10,
    ),
  },
  LTV: {
    DEFAULT: config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
    MAX: config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
    SLIDER_LABELS: generateSliderSteps(
      0,
      config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
      15,
    ),
  },
  CASH: {
    MIN: config.min_cash_downpayment_in_percent.private_residential.first_loan
      .upto_65_financing_year,
  },
};

// for Private Residential
export const FINANCING_YEAR_75 = {
  LOAN_TENURE: {
    DEFAULT: config.max_loan_tenure_in_years.private_residential.upto_75_financing_year,
    MAX: config.max_loan_tenure_in_years.private_residential.upto_75_financing_year,
    SLIDER_LABELS: generateSliderSteps(
      0,
      config.max_loan_tenure_in_years.private_residential.upto_75_financing_year,
      10,
    ),
  },
  LTV: {
    DEFAULT: config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
    MAX: config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
    SLIDER_LABELS: generateSliderSteps(
      0,
      config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
      15,
    ),
  },
  CASH: {
    MIN: config.min_cash_downpayment_in_percent.private_residential.first_loan
      .upto_75_financing_year,
  },
};

// for EC
export const EC_FINANCING_YEAR_65 = {
  LOAN_TENURE: {
    DEFAULT: config.max_loan_tenure_in_years.ec.upto_65_financing_year,
    MAX: config.max_loan_tenure_in_years.ec.upto_65_financing_year,
    SLIDER_LABELS: generateSliderSteps(
      0,
      config.max_loan_tenure_in_years.ec.upto_65_financing_year,
      10,
    ),
  },
  LTV: {
    DEFAULT: config.max_ltv_in_percent.ec.upto_65_financing_year,
    MAX: config.max_ltv_in_percent.ec.upto_65_financing_year,
    SLIDER_LABELS: generateSliderSteps(0, config.max_ltv_in_percent.ec.upto_65_financing_year, 15),
  },
  CASH: {
    MIN: config.min_cash_downpayment_in_percent.ec.upto_65_financing_year,
  },
};

// for EC
export const EC_FINANCING_YEAR_75 = {
  LOAN_TENURE: {
    DEFAULT: config.max_loan_tenure_in_years.ec.upto_75_financing_year,
    MAX: config.max_loan_tenure_in_years.ec.upto_75_financing_year,
    SLIDER_LABELS: generateSliderSteps(
      0,
      config.max_loan_tenure_in_years.ec.upto_75_financing_year,
      10,
    ),
  },
  LTV: {
    DEFAULT: config.max_ltv_in_percent.ec.upto_75_financing_year,
    MAX: config.max_ltv_in_percent.ec.upto_75_financing_year,
    SLIDER_LABELS: generateSliderSteps(0, config.max_ltv_in_percent.ec.upto_75_financing_year, 15),
  },
  CASH: {
    MIN: config.min_cash_downpayment_in_percent.ec.upto_75_financing_year,
  },
};

export const ABSD = {
  LIST: [
    ...new Set([
      config.absd_in_percent.sc.buying_first_property,
      config.absd_in_percent.pr.buying_first_property,
      config.absd_in_percent.sc.buying_second_property,
      config.absd_in_percent.pr.buying_second_property,
      config.absd_in_percent.sc.buying_third_property,
      config.absd_in_percent.pr.buying_third_property,
      config.absd_in_percent.foreigners.buying_first_property,
      config.absd_in_percent.entities,
    ]),
  ]
    .sort((a, b) => a - b)
    .map((value) => ({
      value: value.toString(),
      label: `${value}%`,
    })),
};

export const SSD_LIST = [
  config.ssd_in_percent.residential_property.greater_than_4years,
  config.ssd_in_percent.residential_property.greater_than_3years_and_upto_4years,
  config.ssd_in_percent.residential_property.greater_than_2years_and_upto_3years,
  config.ssd_in_percent.residential_property.greater_than_1year_and_upto_2years,
  config.ssd_in_percent.residential_property.upto_1year,
];

export const SSD_OPTIONS = SSD_LIST.map((value) => {
  return {
    value: value.toString(),
    label: `${value}%`,
  };
});

export const SSD_INDUSTRIAL_LIST = [
  config.ssd_in_percent.industrial.greater_than_3years,
  config.ssd_in_percent.industrial.greater_than_2years_and_upto_3years,
  config.ssd_in_percent.industrial.greater_than_1year_and_upto_2years,
  config.ssd_in_percent.industrial.upto_1year,
];

export const GST_DEFAULT = config.gst_rate;
export const TDSR_RATE = config.tdsr_rate / 100;
export const INCOME_HAIRCUT_PERCENT = config.haircut_rate;
export const MAX_AGE = 74;

export enum PAGE {
  INPUT = 'input-page',
  PREVIEW = 'preview-page',
}

export const BACK = 'Back';
export const RESET = 'Reset';
export const SHARE = 'Share';
export const PREVIEW = 'Preview';
export const DELETE = 'Delete';

export const FORMAT_DD_MM_YYYY = 'DD-MM-YYYY';
export const FORMAT_YYYY_MMM_D = 'YYYY-MMM-D';
export const FORMAT_D_MMM_YYYY = 'D MMM YYYY';
export const FORMAT_DD_MMM_YYYY = 'DD MMM YYYY';
export const FORMAT_MMM_YYYY = 'MMM YYYY';
export const FORMAT_MMM = 'MMM';

export const DEPOSIT_DEFAULT = baseConfig.deposit_in_percent.default;
export const MIN_DEPOSIT = baseConfig.deposit_in_percent.min;
export const MAX_DEPOSIT = baseConfig.deposit_in_percent.max;

export const ADMIN_FEE_NOTE = `ERA Admin fee of $${baseConfig.admin_fee} (if applicable) is not included in this calculation and will be charged separately.`;

export enum TimelineType {
  SALE_TIMELINE = 'Sale Timeline',
  PURCHASE_TIMELINE = 'Purchase Timeline',
}

export enum CalculateName {
  // Parent Calculators
  PRIVATE_RESALE = 'Private Resale',
  COMMERCIAL_INDUSTRIAL = 'Commercial Industrial',
  OTHERS = 'Others',
  HDB_RESALE = 'HDB Resale',

  // Child Calculators
  MSR = 'Affordability (MSR)',
  BUYER_STAMP_DUTY = "Buyer's Stamp Duty",
  PURCHASE_RESALE = 'Purchase Resale',
  PURCHASE_NEW_PROJECT = 'Purchase New Project (BUC)',
  SALES_PROCEEDS = 'Sales Proceeds',
  RENTAL_YIELD = 'Rental Yield',
  TDSR = 'Loan Eligibility (TDSR)',
  COMMERCIAL_INDUSTRIAL_PURCHASE = 'Purchase',
  RESALE_VS_BUC = 'Resale vs BUC',
  INVESTMENT = 'Investment',
  MORTGAGE_LOAN = 'Mortgage Loan Amortisation',
  DECOUPLING = 'Decoupling',
  PART_SHARE_PURCHASE = 'Part Share Purchase',
  EQUITY_TERM_LOAN = 'Equity Term Loan',
  PLEDGE_UNPLEDGE = 'Pledge / Unpledge',
  RENTAL_STAMP_DUTY = 'Rental Stamp Duty',
  HDB_SALES_PROCEEDS = 'HDB Sales Proceeds',
  HDB_RESALE_PURCHASE = 'HDB Resale Purchase',
  CPF_USAGE = 'CPF Usage',
}

export const YES = 'yes';
export const NO = 'no';

export enum YES_NO {
  YES = 'yes',
  NO = 'no',
}

export const YES_NO_LIST = [
  { value: YES_NO.YES, label: 'Yes' },
  { value: YES_NO.NO, label: 'No' },
];

export const getPageName = (pathName: string) => {
  switch (pathName) {
    // Parent Pages
    case Routes.HDB_RESALE: {
      return CalculateName.HDB_RESALE;
    }

    case Routes.PRIVATE_RESALE: {
      return CalculateName.PRIVATE_RESALE;
    }

    case Routes.COMMERCIAL_INDUSTRIAL: {
      return CalculateName.COMMERCIAL_INDUSTRIAL;
    }

    case Routes.OTHERS: {
      return CalculateName.OTHERS;
    }

    // Child Pages
    case Routes.HDB_RESALE + HdbResaleRoutes.PURCHASE:
    case Routes.HDB_RESALE + PreviewRoutes.HDB_RESALE_PURCHASE: {
      return CalculateName.HDB_RESALE_PURCHASE;
    }

    case Routes.HDB_RESALE + HdbResaleRoutes.MSR:
    case Routes.HDB_RESALE + PreviewRoutes.MSR:
    case Routes.OTHERS + HdbResaleRoutes.MSR:
    case Routes.OTHERS + PreviewRoutes.MSR: {
      return CalculateName.MSR;
    }

    case Routes.HDB_RESALE + HdbResaleRoutes.SALES_PROCEEDS: {
      return CalculateName.HDB_SALES_PROCEEDS;
    }

    case PrivateResaleRoutes.PURCHASE_RESALE:
    case PreviewRoutes.PURCHASE_RESALE:
    case Routes.PRIVATE_RESALE + PrivateResaleRoutes.PURCHASE_RESALE:
    case Routes.PRIVATE_RESALE + PreviewRoutes.PURCHASE_RESALE: {
      return CalculateName.PURCHASE_RESALE;
    }

    case PrivateResaleRoutes.BUYER_STAMP_DUTY:
    case PreviewRoutes.BUYER_STAMP_DUTY:
    case Routes.PRIVATE_RESALE + PrivateResaleRoutes.BUYER_STAMP_DUTY:
    case Routes.PRIVATE_RESALE + PreviewRoutes.BUYER_STAMP_DUTY:
    case Routes.OTHERS + OthersRoutes.BUYER_STAMP_DUTY:
    case Routes.OTHERS + PreviewRoutes.BUYER_STAMP_DUTY: {
      return CalculateName.BUYER_STAMP_DUTY;
    }

    case PrivateResaleRoutes.SALES_PROCEEDS:
    case Routes.PRIVATE_RESALE + PrivateResaleRoutes.SALES_PROCEEDS:
    case Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.SALES_PROCEEDS: {
      return CalculateName.SALES_PROCEEDS;
    }

    case PrivateResaleRoutes.TDSR: {
      return CalculateName.TDSR;
    }

    case Routes.PRIVATE_RESALE + PrivateResaleRoutes.TDSR: {
      return CalculateName.TDSR;
    }

    case Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.PURCHASE_RESALE: {
      return CalculateName.COMMERCIAL_INDUSTRIAL_PURCHASE;
    }

    case PrivateResaleRoutes.RENTAL_YIELD:
    case Routes.PRIVATE_RESALE + PrivateResaleRoutes.RENTAL_YIELD:
    case Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.RENTAL_YIELD:
    case Routes.OTHERS + OthersRoutes.RENTAL_YIELD: {
      return CalculateName.RENTAL_YIELD;
    }

    case Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.RESALE_VS_BUC: {
      return CalculateName.RESALE_VS_BUC;
    }

    case Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.INVESTMENT: {
      return CalculateName.INVESTMENT;
    }

    case Routes.OTHERS + OthersRoutes.MORTGAGE_LOAN:
    case Routes.OTHERS + PreviewRoutes.MORTGAGE_LOAN: {
      return CalculateName.MORTGAGE_LOAN;
    }

    case Routes.OTHERS + OthersRoutes.PART_SHARE_PURCHASE:
    case Routes.OTHERS + PreviewRoutes.PART_SHARE_PURCHASE: {
      return CalculateName.DECOUPLING + ' / ' + CalculateName.PART_SHARE_PURCHASE;
    }

    case Routes.OTHERS + OthersRoutes.EQUITY_TERM_LOAN:
    case Routes.OTHERS + PreviewRoutes.EQUITY_TERM_LOAN: {
      return CalculateName.EQUITY_TERM_LOAN;
    }

    case Routes.OTHERS + OthersRoutes.PLEDGE_UNPLEDGE:
    case Routes.OTHERS + PreviewRoutes.PLEDGE_UNPLEDGE: {
      return CalculateName.PLEDGE_UNPLEDGE;
    }

    case Routes.OTHERS + OthersRoutes.RENTAL_STAMP_DUTY:
    case Routes.OTHERS + PreviewRoutes.RENTAL_STAMP_DUTY: {
      return CalculateName.RENTAL_STAMP_DUTY;
    }

    case Routes.OTHERS + OthersRoutes.CPF_USAGE:
    case Routes.OTHERS + PreviewRoutes.CPF_USAGE: {
      return CalculateName.CPF_USAGE;
    }

    case Routes.HDB_RESALE + HdbResaleRoutes.SALES_PROCEEDS:
    case Routes.HDB_RESALE + PreviewRoutes.SALES_PROCEEDS: {
      return CalculateName.HDB_SALES_PROCEEDS;
    }

    case Routes.PRIVATE_RESALE + PrivateResaleRoutes.PURCHASE_NEW_PROJECT:
    case Routes.PRIVATE_RESALE + PreviewRoutes.PURCHASE_NEW_PROJECT: {
      return CalculateName.PURCHASE_NEW_PROJECT;
    }

    default: {
      return 'Back';
    }
  }
};

export const DISCLAIMER_CONSTANTS = {
  TITLE: 'Disclaimer for Calculations',

  BASE_TEXT: `This information is provided solely on a goodwill basis and does not relieve parties of their responsibility to verify the information from the relevant sources and/or seek appropriate advice from relevant professionals such as valuers, financial advisers, bankers and lawyers. For avoidance of doubt, ERA Realty Network and its salesperson accepts no responsibility for the accuracy, reliability and or completeness of the information provided. Copyright in this publication is owned by ERA and this publication may not be reproduced or transmitted in any form or by any means, in whole or in part, without prior written approval.`,

  TDSR_MAS_INFO: `From 30 Sept 2022, MAS require banks to use the HIGHER of a 4% floor rate OR the respective banks interest rate applicable during the tenure of the property loan, excluding introductory or promotional rates, to compute the TDSR & MSR eligible loan amount.`,

  STAMP_DUTY_CALCULATOR_NOTE: `This calculator is intended to estimate the stamp duty payable based on the information provided. It does not constitute an official declaration of stamp duty.`,

  STAMP_DUTY_PAYMENT_INFO: `Stamp Duty has to be paid within 14 days of exercising the OTP. CPF requires 6-8 weeks to process the application to use CPF funds. If insufficient time, stamp duty has to be paid with OWN FUND (ie CASH) first.`,
} as const;

export const CANCELLATION_PENALTY_IN_PERCENT = baseConfig.cancellation_penalty_in_percent;
export const REFUNDABLE_AMOUNT_IN_PERCENT = baseConfig.refundable_amount_in_percent;
export const VALUATION_BREAKDOWN_COLORS = ['#F8CB59', '#9475EB', '#F25A73'];
